import { SearchProduct } from '@winelivery-org/wl-client-sdk-js/dist/models/search';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';
import Modal from 'react-modal';
import {
  EmailShareButton,
  TelegramIcon,
  TelegramShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { Button } from '../../../../../components/design-system';
import { Close, Copy, Mail } from '../../../../../components/icons';
import { useCopyToClipboard, useToggle } from '../../../../../hooks';

interface RenderTriggerArgs {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

interface Props {
  product: SearchProduct;
  renderTrigger: (args: RenderTriggerArgs) => ReactNode;
}

const customStyle = {
  overlay: {
    zIndex: 999,
    backgroundColor: 'rgb(0 0 0 / 20%)',
  },
};

export default function ShareModal(props: Props) {
  const t = useTranslations('shop');
  const { isOpen, open, close } = useToggle();
  const { copy } = useCopyToClipboard();

  function getUrl() {
    return typeof window === 'undefined' ? '' : window.location.href;
  }

  function handleCopy() {
    copy(getUrl());
  }

  return (
    <>
      {props.renderTrigger({ isOpen, open, close })}
      <Modal
        isOpen={isOpen}
        onRequestClose={close}
        style={customStyle}
        className={`
          absolute bg-white p-0
          inset-x-0 top-10 bottom-0 sm:inset-x-40 sm:inset-y-auto
          sm:top-1/2 sm:-translate-y-1/2 sm:max-w-lg sm:mx-auto
          border-t sm:border-none border-greyscale-separator rounded-t-lg sm:rounded-b-lg
        `}
      >
        <div className="py-2 px-4 sm:p-4 flex items-center border-b sm:border-none border-greyscale-separator">
          <div className="flex-1">
            <Button
              className="-ml-1"
              palette="gray"
              theme="ghost"
              onClick={close}
            >
              <Close className="h-5 w-5 stroke-2" />
            </Button>
          </div>
          <h5 className="text-lg sm:text-2xl font-bold tracking-tight flex-auto mb-0 text-center">
            {t('share.title')}
          </h5>
          <div className="flex-1" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-6 sm:pt-0">
          <button
            type="button"
            className="flex items-center gap-4 border border-greyscale-separator rounded-lg p-3 text-greyscale-body text-base tracking-tight font-medium hover:bg-greyscale-background"
            onClick={handleCopy}
          >
            <div className="w-10 h-10 flex items-center justify-center bg-greyscale-input rounded-lg">
              <Copy />
            </div>
            {t('share.copy')}
          </button>
          <EmailShareButton
            resetButtonStyle={false}
            url={getUrl()}
            subject={props.product.name}
            body={t('share.body', { name: props.product.name })}
            className="flex items-center gap-3 border border-greyscale-separator rounded-lg p-3 text-greyscale-body text-base tracking-tight font-medium hover:bg-greyscale-background"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-greyscale-input rounded-lg">
              <Mail />
            </div>
            <span>{t('share.email')}</span>
          </EmailShareButton>
          <WhatsappShareButton
            resetButtonStyle={false}
            url={getUrl()}
            title={t('share.body', { name: props.product.name })}
            className="flex items-center gap-3 border border-greyscale-separator rounded-lg p-3 text-greyscale-body text-base tracking-tight font-medium hover:bg-greyscale-background"
          >
            <WhatsappIcon size={40} className="rounded-lg" />
            <span>{t('share.whatsapp')}</span>
          </WhatsappShareButton>
          <TelegramShareButton
            resetButtonStyle={false}
            url={getUrl()}
            title={t('share.body', { name: props.product.name })}
            className="flex items-center gap-3 border border-greyscale-separator rounded-lg p-3 text-greyscale-body text-base tracking-tight font-medium hover:bg-greyscale-background"
          >
            <TelegramIcon size={40} className="rounded-lg" />
            <span>{t('share.telegram')}</span>
          </TelegramShareButton>
        </div>
      </Modal>
    </>
  );
}
